import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getGitHubCdnUrl } from "../../utils/githubMediaUtils";
import CardServices from "../CardServices";
import { servicesData } from "../../constants/data";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const ServicesSection = () => {
  const location = useLocation();
  const servicesRef = useRef();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  
  const photoUrl = getGitHubCdnUrl("photo/2.jpg");

  const controls = useAnimation();
  const buttonControls = useAnimation();
  const imageControls = useAnimation();

  const [ref, inView] = useInView({ threshold: 0.2 });
  const [headingRef, headingInView] = useInView({ threshold: 0.2 });
  const [buttonRef, buttonInView] = useInView({ threshold: 0.2 });
  
  const [imageError, setImageError] = useState(false);

  // Scroll to ServicesSection if returning from a detail page
  useEffect(() => {
    const handlePopState = () => {
      const fromServicesSection = localStorage.getItem("fromServicesSection");

      if (fromServicesSection) {
        servicesRef.current.scrollIntoView({ behavior: "smooth" });
        localStorage.removeItem("fromServicesSection"); // Clear the flag after scrolling
      }
    };

    // Run the function once on component mount
    handlePopState();

    // Listen for back button (popstate) events
    window.addEventListener("popstate", handlePopState);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Image animation
  useEffect(() => {
    if (inView) {
      imageControls.start({
        scale: 1,
        opacity: 1,
        transition: { duration: 0.8 },
      });
    } else {
      imageControls.start({
        scale: 0.8,
        opacity: 0,
        transition: { duration: 0.8 },
      });
    }
  }, [inView, imageControls]);

  // Heading animation
  useEffect(() => {
    if (headingInView) {
      controls.start({ opacity: 1, y: 0, transition: { duration: 1 } });
    } else {
      controls.start({ opacity: 0, y: 50, transition: { duration: 1 } });
    }
  }, [headingInView, controls]);

  // Button animation
  useEffect(() => {
    if (buttonInView) {
      buttonControls.start("visible");
    } else {
      buttonControls.start("hidden");
    }
  }, [buttonInView, buttonControls]);

  const headingVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const [isRTL, setIsRTL] = useState(false);
  const language = i18n.language;

  useEffect(() => {
    setIsRTL(language !== "en");
  }, [language]);

  return (
    <div
      id="Services"
      ref={servicesRef}
      className="lg:min-h-screen bg-black text-white"
    >
      <div className="container mx-auto">
        <div className="py-16 lg:py-28">
          <div className="flex flex-col lg:flex-row justify-center items-center">
            {/* Image */}
            <motion.div className="rounded-full overflow-hidden md:h-24 h-16 md:w-[400px] w-[250px] mr-4 ml-4 hidden lg:block">
              <motion.img
                src={imageError ? "/images/placeholder.jpg" : photoUrl}
                onError={() => setImageError(true)}
                whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                alt="Team working"
                className="rounded-full md:h-24 h-16 md:w-[400px] w-[250px] object-cover"
              />
            </motion.div>

            {/* Headings */}
            <motion.h1
              className="text-4xl md:text-7xl font-bold"
              initial="hidden"
              animate={controls}
              variants={headingVariants}
              ref={headingRef}
            >
              {t("the_creative")} <span className="font-thin">{t("team")}</span>
            </motion.h1>
          </div>

          <div className="flex flex-col gap-8 lg:flex-row lg:gap-4 justify-center items-center mt-10">
            <motion.h1
              className="text-4xl md:text-6xl font-bold mr-4 ml-4"
              initial="hidden"
              animate={controls}
              variants={headingVariants}
              style={{ marginTop: "-20px" }}
            >
              <span className="font-bold">{t("awaits")}</span>{" "}
              <span className="font-thin">{t("to_you")}</span>
            </motion.h1>
          </div>
        </div>

        <div className="flex justify-center items-center px-5 md:px-px">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-10 lg:gap-20">
            {servicesData.map((service, index) => (
              <CardServices
                key={index}
                title={t(service.title)}
                description={t(service.description)}
                buttonText={t(service.buttonText)}
                cardPath={`/Services/${t(service.title)}`}
                bgImage={service.image}
                features={service.features}
              />
            ))}
          </div>
        </div>

        <div className="py-16"></div>
      </div>
    </div>
  );
};

export default ServicesSection;
